/**
 * Theme variable utilities for managing --jobsite-* CSS variables
 * Provides type-safe getters and setters for all jobsite theme variables
 */

/**
 * All available jobsite theme CSS variables
 */
/**
 * How CTAs are set, site-wide.
 *
 * One stored preset rather than three columns: a theme token has to be listed
 * in nine hand-maintained places (Prisma, two zod schemas, THEME_VARS,
 * DatabaseTheme, mapDatabaseThemeToVars, the editor's two rename maps, and both
 * CSS emitters), none of them type-checked against each other. Three columns
 * would be three times that, and three chances to silently no-op.
 *
 * The expansion lives here so the built site and the editor preview cannot
 * disagree about what "uppercase" means.
 */
export const BUTTON_STYLE_PRESETS = {
  standard: { buttonWeight: "500", buttonTransform: "none", buttonTracking: "normal" },
  uppercase: { buttonWeight: "600", buttonTransform: "uppercase", buttonTracking: "0.06em" },
  heavy: { buttonWeight: "700", buttonTransform: "none", buttonTracking: "-0.01em" },
} as const;

export type ButtonStylePreset = keyof typeof BUTTON_STYLE_PRESETS;

export const DEFAULT_BUTTON_STYLE: ButtonStylePreset = "standard";

/** Unknown or unset falls back to standard rather than dropping the vars. */
export function buttonStyleVars(preset: string | null | undefined): {
  buttonWeight: string;
  buttonTransform: string;
  buttonTracking: string;
} {
  const key = (preset ?? "") as ButtonStylePreset;
  return { ...(BUTTON_STYLE_PRESETS[key] ?? BUTTON_STYLE_PRESETS[DEFAULT_BUTTON_STYLE]) };
}

export const THEME_VARS = {
  // Layout
  radius: "--jobsite-radius",

  // Base colors
  colorBackground: "--jobsite-background",
  colorForeground: "--jobsite-foreground",

  // Card colors
  colorCard: "--jobsite-card",
  colorCardForeground: "--jobsite-card-foreground",

  // Popover colors
  colorPopover: "--jobsite-popover",
  colorPopoverForeground: "--jobsite-popover-foreground",

  // Primary colors
  colorPrimary: "--jobsite-primary",
  colorPrimaryForeground: "--jobsite-primary-foreground",

  // Secondary colors
  colorSecondary: "--jobsite-secondary",
  colorSecondaryForeground: "--jobsite-secondary-foreground",

  // Muted colors
  colorMuted: "--jobsite-muted",
  colorMutedForeground: "--jobsite-muted-foreground",

  // Accent colors
  colorAccent: "--jobsite-accent",
  colorAccentForeground: "--jobsite-accent-foreground",

  // Destructive colors
  colorDestructive: "--jobsite-destructive",
  colorDestructiveForeground: "--jobsite-destructive-foreground",

  // Border and input
  colorBorder: "--jobsite-border",
  colorInput: "--jobsite-input",
  colorRing: "--jobsite-ring",

  // Chart colors
  colorChart1: "--jobsite-chart-1",
  colorChart2: "--jobsite-chart-2",
  colorChart3: "--jobsite-chart-3",
  colorChart4: "--jobsite-chart-4",
  colorChart5: "--jobsite-chart-5",

  // Sidebar colors
  colorSidebar: "--jobsite-sidebar",
  colorSidebarForeground: "--jobsite-sidebar-foreground",
  colorSidebarPrimary: "--jobsite-sidebar-primary",
  colorSidebarPrimaryForeground: "--jobsite-sidebar-primary-foreground",
  colorSidebarAccent: "--jobsite-sidebar-accent",
  colorSidebarAccentForeground: "--jobsite-sidebar-accent-foreground",
  colorSidebarBorder: "--jobsite-sidebar-border",
  colorSidebarRing: "--jobsite-sidebar-ring",

  // Font families
  fontFamilyHeading: "--jobsite-font-heading",
  fontFamilyBody: "--jobsite-font-body",

  // Font weights
  fontWeightHeading: "--jobsite-font-weight-heading",
  // One stored preset, three CSS vars. See BUTTON_STYLE_PRESETS.
  buttonWeight: "--jobsite-button-weight",
  buttonTransform: "--jobsite-button-transform",
  buttonTracking: "--jobsite-button-tracking",
  fontWeightBody: "--jobsite-font-weight-body",
} as const;

/**
 * Type-safe theme variable keys
 */
export type ThemeVarKey = keyof typeof THEME_VARS;

/**
 * Theme variable values type
 */
export type ThemeVarValue = string;

/**
 * Partial theme configuration
 */
export type ThemeConfig = Partial<Record<ThemeVarKey, ThemeVarValue>>;

/**
 * Render a theme as CSS text.
 *
 * The editor used to push these variables into the preview iframe imperatively, polling for
 * the frame and giving up after ~3s, and any Puck remount silently wiped the inline styles it
 * had written — so the panel could say "Regular" while the preview still rendered bold. A
 * stylesheet can't race a render or be wiped by one: it either exists in the document or the
 * document isn't there either.
 *
 * The build renders its own <style> from the same shape, so editor and published site resolve
 * a theme through one code path instead of two that drift.
 */
export function themeVarsToCss(config: ThemeConfig, selector: string = ":root"): string {
  const declarations = Object.entries(config)
    .filter(([, value]) => typeof value === "string" && value.length > 0)
    .map(([key, value]) => `  ${THEME_VARS[key as ThemeVarKey]}: ${value};`)
    .filter((line) => !line.includes("undefined"));

  if (declarations.length === 0) {
    return "";
  }

  return `${selector} {\n${declarations.join("\n")}\n}`;
}

/**
 * Get a single theme variable value
 * @param key - The theme variable key
 * @param element - The element to get the variable from (defaults to document.documentElement)
 * @returns The current value of the theme variable
 */
export function getThemeVar(
  key: ThemeVarKey,
  element: HTMLElement = document.documentElement,
): string {
  const cssVarName = THEME_VARS[key];
  return getComputedStyle(element).getPropertyValue(cssVarName).trim();
}

/**
 * Get all theme variable values
 * @param element - The element to get the variables from (defaults to document.documentElement)
 * @returns An object containing all theme variable values
 */
export function getAllThemeVars(
  element: HTMLElement = document.documentElement,
): Record<ThemeVarKey, string> {
  const result = {} as Record<ThemeVarKey, string>;

  for (const key in THEME_VARS) {
    result[key as ThemeVarKey] = getThemeVar(key as ThemeVarKey, element);
  }

  return result;
}

/**
 * Set a single theme variable value
 * @param key - The theme variable key
 * @param value - The value to set
 * @param element - The element to set the variable on (defaults to document.documentElement)
 */
export function setThemeVar(
  key: ThemeVarKey,
  value: ThemeVarValue,
  element: HTMLElement = document.documentElement,
): void {
  const cssVarName = THEME_VARS[key];
  element.style.setProperty(cssVarName, value);
}

/**
 * Set multiple theme variables at once
 * @param config - Object containing theme variable key-value pairs
 * @param element - The element to set the variables on (defaults to document.documentElement)
 */
export function setThemeVars(
  config: ThemeConfig,
  element: HTMLElement = document.documentElement,
): void {
  for (const key in config) {
    const value = config[key as ThemeVarKey];
    if (value !== undefined) {
      setThemeVar(key as ThemeVarKey, value, element);
    }
  }
}

/**
 * Reset a single theme variable to its default value
 * @param key - The theme variable key
 * @param element - The element to reset the variable on (defaults to document.documentElement)
 */
export function resetThemeVar(
  key: ThemeVarKey,
  element: HTMLElement = document.documentElement,
): void {
  const cssVarName = THEME_VARS[key];
  element.style.removeProperty(cssVarName);
}

/**
 * Reset all theme variables to their default values
 * @param element - The element to reset the variables on (defaults to document.documentElement)
 */
export function resetAllThemeVars(element: HTMLElement = document.documentElement): void {
  for (const key in THEME_VARS) {
    resetThemeVar(key as ThemeVarKey, element);
  }
}

/**
 * Check if a theme variable is set (has an inline value)
 * @param key - The theme variable key
 * @param element - The element to check (defaults to document.documentElement)
 * @returns true if the variable has an inline value set
 */
export function isThemeVarSet(
  key: ThemeVarKey,
  element: HTMLElement = document.documentElement,
): boolean {
  const cssVarName = THEME_VARS[key];
  return element.style.getPropertyValue(cssVarName) !== "";
}

/**
 * Database theme field mapping type
 * Maps database field names (e.g., colorPrimary) to their values
 */
export interface DatabaseTheme {
  colorBackground?: string;
  colorForeground?: string;
  colorCard?: string;
  colorCardForeground?: string;
  colorPopover?: string;
  colorPopoverForeground?: string;
  colorPrimary?: string;
  colorPrimaryForeground?: string;
  colorSecondary?: string;
  colorSecondaryForeground?: string;
  colorMuted?: string;
  colorMutedForeground?: string;
  colorAccent?: string;
  colorAccentForeground?: string;
  colorDestructive?: string;
  colorDestructiveForeground?: string;
  colorBorder?: string;
  colorInput?: string;
  colorRing?: string;
  fontFamilyHeading?: string;
  fontFamilyBody?: string;
  fontWeightHeading?: string;
  buttonStyle?: string;
  fontWeightBody?: string;
  radius?: string;
  // Chart colors (optional)
  colorChart1?: string;
  colorChart2?: string;
  colorChart3?: string;
  colorChart4?: string;
  colorChart5?: string;
  // Sidebar colors (optional)
  colorSidebar?: string;
  colorSidebarForeground?: string;
  colorSidebarPrimary?: string;
  colorSidebarPrimaryForeground?: string;
  colorSidebarAccent?: string;
  colorSidebarAccentForeground?: string;
  colorSidebarBorder?: string;
  colorSidebarRing?: string;
}

/**
 * Convert database theme format to ThemeConfig format
 * Maps database field names (e.g., colorPrimary) to theme-vars keys (e.g., primary)
 * @param dbTheme - Theme object from database
 * @returns ThemeConfig object ready for setThemeVars
 */
export function mapDatabaseThemeToVars(dbTheme: DatabaseTheme): ThemeConfig {
  const config: ThemeConfig = {};

  // Map all color fields
  if (dbTheme.colorBackground !== undefined) {
    config.colorBackground = dbTheme.colorBackground;
  }

  if (dbTheme.colorForeground !== undefined) {
    config.colorForeground = dbTheme.colorForeground;
  }

  if (dbTheme.colorCard !== undefined) {
    config.colorCard = dbTheme.colorCard;
  }
  if (dbTheme.colorCardForeground !== undefined) {
    config.colorCardForeground = dbTheme.colorCardForeground;
  }
  if (dbTheme.colorPopover !== undefined) {
    config.colorPopover = dbTheme.colorPopover;
  }
  if (dbTheme.colorPopoverForeground !== undefined) {
    config.colorPopoverForeground = dbTheme.colorPopoverForeground;
  }
  if (dbTheme.colorPrimary !== undefined) {
    config.colorPrimary = dbTheme.colorPrimary;
  }
  if (dbTheme.colorPrimaryForeground !== undefined) {
    config.colorPrimaryForeground = dbTheme.colorPrimaryForeground;
  }
  if (dbTheme.colorSecondary !== undefined) {
    config.colorSecondary = dbTheme.colorSecondary;
  }
  if (dbTheme.colorSecondaryForeground !== undefined) {
    config.colorSecondaryForeground = dbTheme.colorSecondaryForeground;
  }
  if (dbTheme.colorMuted !== undefined) {
    config.colorMuted = dbTheme.colorMuted;
  }
  if (dbTheme.colorMutedForeground !== undefined) {
    config.colorMutedForeground = dbTheme.colorMutedForeground;
  }
  if (dbTheme.colorAccent !== undefined) {
    config.colorAccent = dbTheme.colorAccent;
  }

  if (dbTheme.colorAccentForeground !== undefined) {
    config.colorAccentForeground = dbTheme.colorAccentForeground;
  }
  if (dbTheme.colorDestructive !== undefined) {
    config.colorDestructive = dbTheme.colorDestructive;
  }

  if (dbTheme.colorDestructiveForeground !== undefined) {
    config.colorDestructiveForeground = dbTheme.colorDestructiveForeground;
  }
  if (dbTheme.colorBorder !== undefined) {
    config.colorBorder = dbTheme.colorBorder;
  }
  if (dbTheme.colorInput !== undefined) {
    config.colorInput = dbTheme.colorInput;
  }
  if (dbTheme.colorRing !== undefined) {
    config.colorRing = dbTheme.colorRing;
  }
  // Map font fields
  if (dbTheme.fontFamilyHeading !== undefined) {
    config.fontFamilyHeading = dbTheme.fontFamilyHeading;
  }

  if (dbTheme.fontFamilyBody !== undefined) {
    config.fontFamilyBody = dbTheme.fontFamilyBody;
  }

  if (dbTheme.buttonStyle !== undefined) {
    Object.assign(config, buttonStyleVars(dbTheme.buttonStyle));
  }
  if (dbTheme.fontWeightHeading !== undefined) {
    config.fontWeightHeading = dbTheme.fontWeightHeading;
  }

  if (dbTheme.fontWeightBody !== undefined) {
    config.fontWeightBody = dbTheme.fontWeightBody;
  }

  if (dbTheme.radius !== undefined) {
    config.radius = dbTheme.radius;
  }

  // Map chart colors (if provided)
  if (dbTheme.colorChart1 !== undefined) {
    config.colorChart1 = dbTheme.colorChart1;
  }
  if (dbTheme.colorChart2 !== undefined) {
    config.colorChart2 = dbTheme.colorChart2;
  }
  if (dbTheme.colorChart3 !== undefined) {
    config.colorChart3 = dbTheme.colorChart3;
  }
  if (dbTheme.colorChart4 !== undefined) {
    config.colorChart4 = dbTheme.colorChart4;
  }
  if (dbTheme.colorChart5 !== undefined) {
    config.colorChart5 = dbTheme.colorChart5;
  }

  if (dbTheme.colorSidebar !== undefined) {
    config.colorSidebar = dbTheme.colorSidebar;
  }
  if (dbTheme.colorSidebarForeground !== undefined) {
    config.colorSidebarForeground = dbTheme.colorSidebarForeground;
  }
  if (dbTheme.colorSidebarPrimary !== undefined) {
    config.colorSidebarPrimary = dbTheme.colorSidebarPrimary;
  }
  if (dbTheme.colorSidebarPrimaryForeground !== undefined) {
    config.colorSidebarPrimaryForeground = dbTheme.colorSidebarPrimaryForeground;
  }
  if (dbTheme.colorSidebarAccent !== undefined) {
    config.colorSidebarAccent = dbTheme.colorSidebarAccent;
  }

  return config;
}
